import React, { useEffect, useRef, useState } from 'react';

import $image_url from 'js/utils/image_url';
import { isEligible, pageOffersFindReaders } from 'js/find_readers/eligibility';

/**
 * The "Find Readers" CTA for REACT surfaces — the LISWP block hero and the
 * analytics Papers table. The Slim surfaces render
 * `Works::FindReadersButtonComponent` instead; this emits the SAME DOM, so both
 * are styled by one stylesheet (academia_components/find_readers_button.scss,
 * loaded site-wide) and driven by one script (js/profiles/setUpFindReaders,
 * which binds a delegated click listener per page and finds buttons by their
 * `js-` classes).
 *
 * Keeping the markup identical is what makes that possible — the classes below
 * are a contract with those two files, not decoration.
 *
 * ## Why it decides for itself whether to appear
 *
 * Rendering nothing until the server has confirmed this viewer may be offered
 * THIS paper is the React equivalent of the Slim component's inline
 * `display:none`: both fail closed, so a missing stylesheet, a dead request or
 * no response leaves no button rather than one that cannot do anything.
 *
 * Per-work eligibility cannot be decided by the caller. On the analytics table
 * the answer differs per row, and on the LISWP the hero is prerendered, so
 * either way it has to be asked for client-side. See FindReaders::Eligibility
 * for the rules and js/find_readers/eligibility for the batching.
 */

// Material Symbols "menu_book" at a lighter weight with opaque pages. The same
// asset Works::FindReadersButtonComponent::ICON_PATH draws, so the two surfaces
// render one button rather than two that merely resemble each other.
const ICON_PATH = 'find_readers/menu_book_filled_pages.svg';

// Picked up by setUpFindReaders' impression watcher, which cannot scan for
// these on DOMContentLoaded: they appear only once eligibility resolves.
const SHOWN_EVENT = 'find-readers:shown';

export type FindReadersButtonProps = {
  /**
   * The work this CTA points at. Accepts the string form because the LISWP
   * serializer's `WorkId` is `string | number`; it is normalized to an integer
   * here rather than at each call site, since the eligibility endpoint keys its
   * response on integers and setUpFindReaders parseInt()s the id back off the
   * DOM.
   */
  workId: number | string;
  /**
   * Impression and click source for this surface, e.g.
   * `liswp-hero-find-readers`. Read off the DOM by setUpFindReaders so one
   * delegated listener can serve every surface. Redshift stores these in a
   * varchar(64) and COPY runs TRUNCATECOLUMNS, so keep them well inside it — an
   * over-long name silently merges with its neighbours.
   */
  source: string;
  /**
   * `trigger` on the upgrade URL a free member is sent to, which distinguishes
   * this door into the feature from the others and rides through to checkout.
   */
  upgradeTrigger: string;
  className?: string;
};

export const FindReadersButton: React.FC<FindReadersButtonProps> = ({
  workId,
  source,
  upgradeTrigger,
  className,
}) => {
  // The work the stored answer belongs to, NOT a boolean. The analytics Papers
  // table re-sorts its rows in place, so one mounted instance is handed a
  // different workId as the author sorts; a boolean would keep the previous
  // paper's "yes" until the new request landed, showing a button for a paper
  // nobody had confirmed — and one that would dispatch against the NEW id.
  // Comparing against the current prop makes a stale answer unrenderable
  // instead of merely short-lived.
  const [eligibleWorkId, setEligibleWorkId] = useState<number | null>(null);
  const ctaRef = useRef<HTMLSpanElement>(null);

  const id = typeof workId === 'number' ? workId : parseInt(workId, 10);
  const eligible = Number.isInteger(id) && eligibleWorkId === id;

  useEffect(() => {
    // The page gate answers for the VIEWER, and a page that has not set it is
    // one where nobody is offered the button — so this costs nothing on the
    // overwhelming majority of views, which are not an author's own.
    if (!Number.isInteger(id) || !pageOffersFindReaders()) return undefined;

    let live = true;
    void isEligible(id).then((answer) => {
      if (live && answer) setEligibleWorkId(id);
    });

    return () => {
      live = false;
    };
  }, [id]);

  useEffect(() => {
    const cta = ctaRef.current;
    if (!eligible || !cta) return;

    document.dispatchEvent(
      new CustomEvent(SHOWN_EVENT, { detail: { el: cta } }),
    );
  }, [eligible]);

  if (!eligible) return null;

  return (
    <span
      ref={ctaRef}
      className={['find-readers-cta', 'js-find-readers-cta', className]
        .filter(Boolean)
        .join(' ')}
      data-work-id={id}
      data-find-readers-source={source}
      data-find-readers-trigger={upgradeTrigger}
    >
      {/* eslint-disable-next-line no-restricted-syntax -- Not a DS Button: the left-pointing pennant shape and premium-orange fill are unique to this CTA, and it must emit the exact markup academia_components/find_readers_button.scss and setUpFindReaders already target. See Works::FindReadersButtonComponent, which makes the same call. */}
      <button
        type="button"
        className="find-readers-button js-find-readers-button"
        data-work-id={id}
      >
        {/* alt="" because the button already says what it does. */}
        <img
          className="find-readers-button__icon"
          src={$image_url(ICON_PATH)}
          alt=""
        />
        <span className="find-readers-button__label">Find Readers</span>
      </button>
    </span>
  );
};

export default FindReadersButton;
